import { useMemo, useState } from 'react';

import { Dialog, DialogContent, Grid } from '@mui/material';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import CustomModal from 'src/components/CustomModal';
import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { Hierarchy, USER_ROLES_MODAL_TABLE_HEADERS, USER_ROLES_TABLE_HEADERS } from 'src/constants';
import { DELETE_USER_ROLE_MUTATION } from 'src/graphql/mutations/userRoles.mutation';
import { GET_ROLES } from 'src/graphql/queries/roles.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { HeadCell } from 'src/types';
import { UserRoleObject } from 'src/types/edit-user-role';
import {
    FilterOptionsType,
    updateTableFilters,
    userRolesFilteredTableData,
} from 'src/utils/tableUtils';

import EditUserRolePage from '../EditUserRolePage/EditUserRolePage';

const RolesPage = () => {
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [userRoleId, setUserRoleId] = useState<number | null>();
    const [userRoleUsers, setUserRoleUsers] = useState<number | null>();
    // set a user role into state when attempting to edit/view that user role
    const [userRoleObject, setUserRoleObject] = useState<UserRoleObject | null>();
    // track the edit role state used to change which component is rendered
    const [showEditPage, setShowEditPage] = useState(false);

    const [selectedRow, setSelectedRow] = useState(null);
    const [tableFilters, setTableFilters] = useState({});
    // GraphQL query to fetch list of user roles
    const { data, loading, refetch } = useQuery(GET_ROLES, {
        fetchPolicy: 'cache-and-network',
    });

    // GraphQL query to delete a user role
    const [deleteRole] = useMutation(DELETE_USER_ROLE_MUTATION, {
        onCompleted: () => {
            showSuccessToast('User Role Deleted Successfully');
            refetch();
        },
        onError: (error) => {
            showErrorToast(error.message);
        },
    });

    // Memoized roles data
    const rolesData = useMemo(() => {
        return userRolesFilteredTableData(data, tableFilters);
    }, [data, tableFilters]);

    const filterOptions = () => {
        const role = data?.roles ?? [];
        return Object.fromEntries([
            [
                'User Role',
                Array.from(new Set(role.map(({ name }) => name))).map((name) => ({
                    value: name,
                    label: name,
                })),
            ],
            [
                'Hierarchy Level',
                Object.keys(Hierarchy).map((key) => ({
                    value: Hierarchy[`${key}`],
                    label: Hierarchy[`${key}`].replaceAll('_', ' '),
                })),
            ],
        ]) as FilterOptionsType;
    };

    const handleCellClick = (event, rowData) => {
        setSelectedRow(rowData);
    };

    const addNewRole = () => {
        navigate('/user-management/add-user-role');
    };

    const viewRole = ({ id, name, hierarchyLevel, permissions }: UserRoleObject) => {
        setUserRoleObject({
            id,
            name,
            hierarchyLevel,
            permissions,
        });

        setShowEditPage(true);
    };

    const closeEditPage = () => {
        setShowEditPage(false);
    };

    const handleOpenDeleteModal = (recordId) => {
        const roleUsers = data?.roles.find((role) => role.id === recordId);

        setUserRoleId(recordId);
        setUserRoleUsers(roleUsers.users.length);
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    const handleDeleteRecord = () => {
        deleteRole({ variables: { id: userRoleId } });
        handleCloseDeleteModal();
    };

    return (
        <>
            {showEditPage ? (
                <EditUserRolePage
                    userRoleObject={userRoleObject}
                    closeEditPage={closeEditPage}
                    refetch={refetch}
                />
            ) : (
                <PageContent
                    heading="User Roles"
                    subHeading="View and manage the user role types available on Buddy"
                >
                    <MetaTags title="Roles" description="Roles page" />

                    {/** Delete modal */}
                    <Dialog
                        open={openDeleteModal}
                        onClose={handleCloseDeleteModal}
                        aria-labelledby="delete-user-role-dialog-title"
                        sx={{
                            '& .MuiDialog-paper': {
                                width: '400px',
                            },
                        }}
                    >
                        <DialogContent>
                            <ModalContent
                                handleClose={handleCloseDeleteModal}
                                handleAction={handleDeleteRecord}
                                header="Delete User Role?"
                                message={
                                    userRoleUsers > 0
                                        ? `This user role cannot be deleted as it currently has ${userRoleUsers} ${
                                              userRoleUsers > 1 ? 'users' : 'user'
                                          } associated with it. Please remove the associated users and then try again`
                                        : 'Are you sure you want to permanently delete this user role? The user role cannot be restored once deleted'
                                }
                                buttonText="Delete"
                                isActionDisabled={userRoleUsers > 0}
                            />
                        </DialogContent>
                    </Dialog>

                    {/** Users list modal */}
                    {selectedRow && selectedRow.users > 0 && (
                        <CustomModal
                            open={Boolean(selectedRow)}
                            handleClose={() => setSelectedRow(null)}
                            title={`Users for ${selectedRow.role} role`}
                            isCentered={true}
                            isTableModal
                        >
                            <TableList
                                data={selectedRow.usersRecord}
                                headers={USER_ROLES_MODAL_TABLE_HEADERS as HeadCell[]}
                                isTableModal
                            />
                        </CustomModal>
                    )}

                    <Grid container sx={{ gap: '24px' }}>
                        <Grid item xs={12}>
                            {loading || !data ? (
                                <Loader />
                            ) : (
                                <TableList
                                    addRecordButtonText="Add User Role"
                                    data={rolesData}
                                    headers={USER_ROLES_TABLE_HEADERS as HeadCell[]}
                                    title="Roles List"
                                    addRecordCallBack={addNewRole}
                                    handleViewRecord={viewRole}
                                    deleteRecordCallBack={handleOpenDeleteModal}
                                    enableFilter
                                    handleCellClick={handleCellClick}
                                    enableExport
                                    filterOptions={filterOptions()}
                                    updateTableFilters={(filter: string, newFilterValue: string) =>
                                        setTableFilters((prevFilters) =>
                                            updateTableFilters(prevFilters, newFilterValue, filter)
                                        )
                                    }
                                />
                            )}
                        </Grid>
                    </Grid>
                </PageContent>
            )}
        </>
    );
};

export default RolesPage;
